<div class="comments-container">
  <!-- Loading overlay -->
  @if (loading) {
    <div class="loader">
      <div class="spinner"></div>
    </div>
  }

  <!-- Comments List -->
  @if (!loading) {
    <div class="comments-list">
      @for (comment of comments; track comment) {
        <div class="comment-item">
          <span class="comment-avatar" aria-hidden="true">{{ getAuthorInitials(comment.createdBy) }}</span>
          <!-- Comment Header -->
          <div class="comment-header">
            <div class="comment-author-info">
              <span class="comment-author" [title]="getCommentAuthorTitle(comment.createdBy)">
                {{ getCommentAuthor(comment.createdBy) }}
              </span>
              <span class="comment-timestamp">{{ comment.createdOn | date:'medium' }}</span>
            </div>
            @if (comment.canEdit || comment.canDelete) {
              <div class="comment-actions">
                @if (comment.canEdit && !comment.isEditing) {
                  <button
                    class="icon-btn"
                    (click)="startEditing(comment)"
                    title="Edit Comment"
                    aria-label="Edit Comment">
                    <i class="mdi mdi-pencil"></i>
                  </button>
                }
                @if (comment.canDelete) {
                  <button
                    class="icon-btn is-danger"
                    (click)="deleteComment(comment)"
                    title="Delete Comment"
                    aria-label="Delete Comment">
                    <i class="mdi mdi-delete"></i>
                  </button>
                }
              </div>
            }
          </div>
          <!-- Comment Content (Display Mode) -->
          @if (!comment.isEditing) {
            <div class="comment-content">{{ comment.commentText }}</div>
          }
          <!-- Comment Content (Edit Mode) -->
          @if (comment.isEditing) {
            <div class="comment-edit-form">
              <div class="mb-2">
                <textarea
                  class="form-control"
                  [(ngModel)]="comment.editText"
                  rows="3"
                  placeholder="Edit your comment...">
                </textarea>
              </div>
              <div class="button-items">
                <button
                  class="btn btn-sm btn-primary"
                  (click)="editComment(comment)"
                  [disabled]="!comment.editText || comment.editText.trim() === ''">
                  <i class="mdi mdi-content-save"></i> Save
                </button>
                <button
                  class="btn btn-sm btn-secondary"
                  (click)="cancelEditing(comment)">
                  Cancel
                </button>
              </div>
            </div>
          }
          <!-- Comment Footer -->
          @if (!comment.isEditing) {
            <div class="comment-footer">
              <button
                class="reply-link"
                (click)="toggleReplyForm(comment)">Reply
              </button>
              @if (comment.replies && comment.replies.length > 0) {
                <button
                  class="reply-link"
                  [class.is-muted]="!comment.isCollapsed"
                  [attr.aria-expanded]="!comment.isCollapsed"
                  (click)="toggleCollapse(comment)">
                  {{ comment.isCollapsed ? 'View ' + comment.replies.length + (comment.replies.length === 1 ? ' reply' : ' replies') : 'Hide replies' }}
                </button>
              }
            </div>
          }
          <!-- Reply Form -->
          @if (comment.showReplyForm) {
            <div class="reply-form">
              <div class="mb-2">
                <textarea
                  class="form-control"
                  [(ngModel)]="comment.replyText"
                  rows="2"
                  placeholder="Write a reply...">
                </textarea>
              </div>
              <div class="button-items">
                <button
                  class="btn btn-sm btn-primary"
                  (click)="addReply(comment)"
                  [disabled]="!comment.replyText || comment.replyText.trim() === ''">
                  <i class="mdi mdi-send"></i> Reply
                </button>
                <button
                  class="btn btn-sm btn-secondary"
                  (click)="cancelReply(comment)">
                  Cancel
                </button>
              </div>
            </div>
          }
          <!-- Nested Replies -->
          @if (comment.replies && comment.replies.length > 0 && !comment.isCollapsed) {
            <div class="comment-replies">
              @for (reply of comment.replies; track reply) {
                <div class="reply-item">
                  <span class="comment-avatar is-small" aria-hidden="true">{{ getAuthorInitials(reply.createdBy) }}</span>
                  <!-- Reply Header -->
                  <div class="comment-header">
                    <div class="comment-author-info">
                      <span class="comment-author" [title]="getCommentAuthorTitle(reply.createdBy)">
                        {{ getCommentAuthor(reply.createdBy) }}
                      </span>
                      <span class="comment-timestamp">{{ reply.createdOn | date:'medium' }}</span>
                      @if (reply.isResolved) {
                        <span class="comment-status">
                          <i class="mdi mdi-check-circle text-success"></i> Resolved
                        </span>
                      }
                    </div>
                    @if (reply.canEdit || reply.canDelete) {
                      <div class="comment-actions">
                        @if (reply.canEdit && !reply.isEditing) {
                          <button
                            class="icon-btn"
                            (click)="startEditing(reply)"
                            title="Edit Reply"
                            aria-label="Edit Reply">
                            <i class="mdi mdi-pencil"></i>
                          </button>
                        }
                        @if (reply.canDelete) {
                          <button
                            class="icon-btn is-danger"
                            (click)="deleteComment(reply)"
                            title="Delete Reply"
                            aria-label="Delete Reply">
                            <i class="mdi mdi-delete"></i>
                          </button>
                        }
                      </div>
                    }
                  </div>
                  <!-- Reply Content (Display Mode) -->
                  @if (!reply.isEditing) {
                    <div class="comment-content">{{ reply.commentText }}</div>
                  }
                  <!-- Reply Content (Edit Mode) -->
                  @if (reply.isEditing) {
                    <div class="comment-edit-form">
                      <div class="mb-2">
                        <textarea
                          class="form-control"
                          [(ngModel)]="reply.editText"
                          rows="2"
                          placeholder="Edit your reply...">
                        </textarea>
                      </div>
                      <div class="button-items">
                        <button
                          class="btn btn-sm btn-primary"
                          (click)="editComment(reply)"
                          [disabled]="!reply.editText || reply.editText.trim() === ''">
                          <i class="mdi mdi-content-save"></i> Save
                        </button>
                        <button
                          class="btn btn-sm btn-secondary"
                          (click)="cancelEditing(reply)">
                          Cancel
                        </button>
                      </div>
                    </div>
                  }
                  <!-- Reply Footer with Reply Button -->
                  @if (!reply.isEditing) {
                    <div class="comment-footer">
                      <button
                        class="reply-link"
                        (click)="toggleReplyForm(reply)">Reply
                      </button>
                      @if (reply.replies && reply.replies.length > 0) {
                        <button
                          class="reply-link"
                          [class.is-muted]="!reply.isCollapsed"
                          [attr.aria-expanded]="!reply.isCollapsed"
                          (click)="toggleCollapse(reply)">
                          {{ reply.isCollapsed ? 'View ' + reply.replies.length + (reply.replies.length === 1 ? ' reply' : ' replies') : 'Hide replies' }}
                        </button>
                      }
                    </div>
                  }
                  <!-- Nested Reply Form -->
                  @if (reply.showReplyForm) {
                    <div class="reply-form">
                      <div class="mb-2">
                        <textarea
                          class="form-control"
                          [(ngModel)]="reply.replyText"
                          rows="2"
                          placeholder="Write a reply...">
                        </textarea>
                      </div>
                      <div class="button-items">
                        <button
                          class="btn btn-sm btn-primary"
                          (click)="addReply(reply)"
                          [disabled]="!reply.replyText || reply.replyText.trim() === ''">
                          <i class="mdi mdi-send"></i> Reply
                        </button>
                        <button
                          class="btn btn-sm btn-secondary"
                          (click)="cancelReply(reply)">
                          Cancel
                        </button>
                      </div>
                    </div>
                  }
                  <!-- Deeper Nested Replies (Recursive) -->
                  @if (reply.replies && reply.replies.length > 0 && !reply.isCollapsed) {
                    <div class="comment-replies">
                      <ng-container *ngTemplateOutlet="nestedReplies; context: { $implicit: reply.replies }"></ng-container>
                    </div>
                  }
                </div>
              }
            </div>
          }
        </div>
      }
      <!-- No Comments Message -->
      @if (comments.length === 0) {
        <div class="no-comments">
          <p class="text-muted text-center">
            <i class="mdi mdi-comment-text-outline"></i>
            No comments yet. Be the first to add a comment!
          </p>
        </div>
      }
    </div>
  }


  <div class="add-comment-form">
    <form [formGroup]="commentForm" (ngSubmit)="addComment()">
      <textarea
        class="form-control"
        formControlName="commentText"
        rows="2"
        aria-label="Add a comment"
        placeholder="Write your comment here..."
        [class.is-invalid]="submitted && commentText && commentText.invalid"
        appSpellCheck>
      </textarea>
      @if (submitted && commentText && commentText.invalid) {
        <div class="invalid-feedback">
          @if (commentText.errors && commentText.errors.required) {
            <span>Comment is required</span>
          }
          @if (commentText.errors && commentText.errors.minlength) {
            <span>Comment must be at least 1 character</span>
          }
          @if (commentText.errors && commentText.errors.maxlength) {
            <span>Comment must be less than 1000 characters</span>
          }
        </div>
      }
      <div class="composer-actions">
        <button
          type="button"
          class="btn btn-sm btn-light"
          (click)="commentForm.reset(); submitted = false;">
          Clear
        </button>
        <button
          type="submit"
          class="btn btn-sm btn-primary"
          [disabled]="loading">
          Submit comment
          @if (loading) {
            <i class="fa fa-solid fa-sync fa-spin"></i>
          }
        </button>
      </div>
    </form>
  </div>
</div>

<app-confirmation-alert [showAlert]="!!pendingDeleteComment" message="Are you sure you want to delete this comment?"
  (response)="onDeleteConfirm($event)"></app-confirmation-alert>

<!-- Recursive Template for Deeper Nested Replies -->
<ng-template #nestedReplies let-replies>
  @for (nestedReply of replies; track nestedReply) {
    <div class="reply-item" [class.resolved]="nestedReply.isResolved">
      <span class="comment-avatar is-small" aria-hidden="true">{{ getAuthorInitials(nestedReply.createdBy) }}</span>
      <!-- Nested Reply Header -->
      <div class="comment-header">
        <div class="comment-author-info">
          <span class="comment-author" [title]="getCommentAuthorTitle(nestedReply.createdBy)">
            {{ getCommentAuthor(nestedReply.createdBy) }}
          </span>
          <span class="comment-timestamp">{{ nestedReply.createdOn | date:'medium' }}</span>
          @if (nestedReply.isResolved) {
            <span class="comment-status">
              <i class="mdi mdi-check-circle text-success"></i> Resolved
            </span>
          }
        </div>
        @if (nestedReply.canEdit || nestedReply.canDelete) {
          <div class="comment-actions">
            @if (nestedReply.canEdit && !nestedReply.isEditing) {
              <button
                class="icon-btn"
                (click)="startEditing(nestedReply)"
                title="Edit Reply"
                aria-label="Edit Reply">
                <i class="mdi mdi-pencil"></i>
              </button>
            }
            @if (nestedReply.canDelete) {
              <button
                class="icon-btn is-danger"
                (click)="deleteComment(nestedReply)"
                title="Delete Reply"
                aria-label="Delete Reply">
                <i class="mdi mdi-delete"></i>
              </button>
            }
          </div>
        }
      </div>
      <!-- Nested Reply Content (Display Mode) -->
      @if (!nestedReply.isEditing) {
        <div class="comment-content">{{ nestedReply.commentText }}</div>
      }
      <!-- Nested Reply Content (Edit Mode) -->
      @if (nestedReply.isEditing) {
        <div class="comment-edit-form">
          <div class="mb-2">
            <textarea
              class="form-control"
              [(ngModel)]="nestedReply.editText"
              rows="2"
              placeholder="Edit your reply...">
            </textarea>
          </div>
          <div class="button-items">
            <button
              class="btn btn-sm btn-primary"
              (click)="editComment(nestedReply)"
              [disabled]="!nestedReply.editText || nestedReply.editText.trim() === ''">
              <i class="mdi mdi-content-save"></i> Save
            </button>
            <button
              class="btn btn-sm btn-secondary"
              (click)="cancelEditing(nestedReply)">
              Cancel
            </button>
          </div>
        </div>
      }
      <!-- Nested Reply Footer with Reply Button -->
      @if (!nestedReply.isEditing) {
        <div class="comment-footer">
          @if (!nestedReply.isResolved) {
            <button
              class="reply-link"
              (click)="toggleReplyForm(nestedReply)">Reply
            </button>
          }
          @if (nestedReply.replies && nestedReply.replies.length > 0) {
            <button
              class="reply-link"
              [class.is-muted]="!nestedReply.isCollapsed"
              [attr.aria-expanded]="!nestedReply.isCollapsed"
              (click)="toggleCollapse(nestedReply)">
              {{ nestedReply.isCollapsed ? 'View ' + nestedReply.replies.length + (nestedReply.replies.length === 1 ? ' reply' : ' replies') : 'Hide replies' }}
            </button>
          }
        </div>
      }
      <!-- Nested Reply Form -->
      @if (nestedReply.showReplyForm && !nestedReply.isResolved) {
        <div class="reply-form">
          <div class="mb-2">
            <textarea
              class="form-control"
              [(ngModel)]="nestedReply.replyText"
              rows="2"
              placeholder="Write a reply...">
            </textarea>
          </div>
          <div class="button-items">
            <button
              class="btn btn-sm btn-primary"
              (click)="addReply(nestedReply)"
              [disabled]="!nestedReply.replyText || nestedReply.replyText.trim() === ''">
              <i class="mdi mdi-send"></i> Reply
            </button>
            <button
              class="btn btn-sm btn-secondary"
              (click)="cancelReply(nestedReply)">
              Cancel
            </button>
          </div>
        </div>
      }
      <!-- Even Deeper Nested Replies (Recursive Call) -->
      @if (nestedReply.replies && nestedReply.replies.length > 0 && !nestedReply.isCollapsed) {
        <div class="comment-replies">
          <ng-container *ngTemplateOutlet="nestedReplies; context: { $implicit: nestedReply.replies }"></ng-container>
        </div>
      }
    </div>
  }
</ng-template>
